"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveCV } from "../../services/cvUploadService";
import { closePopup, resetForm, setActiveTab } from "../../store/features/cvUpload/cvUploadSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useToast } from "@/hooks/use-toast";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AxiosError } from "axios";

export function ReviewTab() {
  const dispatch = useAppDispatch();
  const { extractedData } = useAppSelector((state) => state.cvUpload);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const saveMutation = useMutation({
    mutationFn: saveCV,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });

      toast({
        title: "Success",
        description: "CV data saved to database with timestamp",
        variant: "default",
      });
      dispatch(resetForm());
      dispatch(closePopup());
    },
    onError: (error: AxiosError<{ message: string }>) => {
      console.error("Error saving CV data:", error);
      toast({
        title: "Error",
        description: error.response?.data.message,
        variant: "destructive",
      });
    },
  });
  
  const handleSave = () => {
    if (extractedData) {
      saveMutation.mutate(extractedData);
    }
  };
  
  if (!extractedData) {
    return (
      <div className="p-6 text-center">
        <p>No data to review. Please upload or enter CV data first.</p>
        <Button 
          className="mt-4" 
          variant="outline" 
          onClick={() => dispatch(setActiveTab("upload"))}
        >
          Back to Upload
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Name:</span>
              <span className="text-sm col-span-2">{extractedData.name}</span>
            </div>
            {extractedData.birthYear && (
              <div className="grid grid-cols-3 gap-1">
                <span className="text-sm font-medium">Birth Year:</span>
                <span className="text-sm col-span-2">{extractedData.birthYear}</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Email:</span>
              <span className="text-sm col-span-2">
                {/* Masked email for privacy */}
                {extractedData.email?.replace(/(.{2})(.*)(@.*)/, "$1****$3")}
                <Badge variant="outline" className="ml-2 text-xs">
                  Masked
                </Badge>
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Mobile Number:</span>
              <span className="text-sm col-span-2">
                {/* Masked phone for privacy */}
                {extractedData.phone?.replace(/(\d{3})(\d*)(\d{2})/, "$1*****$3")}
                <Badge variant="outline" className="ml-2 text-xs">
                  Masked
                </Badge>
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Preferred Location:</span>
              <span className="text-sm col-span-2">{extractedData.preferredLocation}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Professional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Position:</span>
              <span className="text-sm col-span-2">{extractedData.position}</span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Level:</span>
              <span className="text-sm col-span-2">{extractedData.level}</span>
            </div>
            <div className="grid grid-cols-3 gap-1">
              <span className="text-sm font-medium">Years of Experience:</span>
              <span className="text-sm col-span-2">{extractedData.yearsOfExperience} years</span>
            </div>
            {extractedData.managementExperience && (
              <div className="grid grid-cols-3 gap-1">
                <span className="text-sm font-medium">Management Experience:</span>
                <span className="text-sm col-span-2">{extractedData.managementExperience} years</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Languages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-1">
              {extractedData.languages?.map((lang, index) => (
                <div key={index} className="flex items-center">
                  <Badge variant="secondary" className="mr-2">
                    {lang.language}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{["Beginner", "Basic", "Intermediate", "Fluent", "Native"][lang.level - 1]}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Skills</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {extractedData.skills?.map((skill) => (
                <Badge key={skill} variant="secondary" className="mr-1 mb-1">
                  {skill}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {extractedData.experience && extractedData.experience.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">Experience</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc pl-5 space-y-1">
                {extractedData.experience.map((exp, index) => (
                  <li key={index} className="text-sm">
                    {exp}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={() => dispatch(setActiveTab("profile"))}>
          Edit Information
        </Button>
        <Button 
          onClick={handleSave}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? "Saving..." : "Save to Database"}
        </Button>
      </div>
    </div>
  );
}